// REX2 Export — Real ReCycle-compatible .rx2 file builder
//
// Ported from breakchop's exportRx2() + detectTransients().
// Uses the DWOP encoder from rex2-dwop.cjs for lossless compression.
//
// REX2 file structure (big-endian IFF):
//   CAT <size> 'REX2'
//     HEAD <size> <magic> <version> <zeros> <filename>
//     GLOB <size> <ppq> <timeSig> <beats> ... <tempo> <flags>
//     RECY <size> <loopData>
//     CAT <size> 'DEVL'
//       TRSH <size> <threshold>
//       EQ   <size> <eqSettings>
//       COMP <size> <compSettings>
//     CAT <size> 'SLCL'
//       SLCE <size> <marker> <length> <gain> <flags>  (one per slice)
//     SINF <size> <channels> <depth> <rate> <frames> <loopStart> <loopEnd>
//     SDAT <size> <DWOP-compressed sample data>

import { encodeMono, encodeStereo } from '../../common/rex2-dwop.cjs';

// ---- Transient detection (ported from breakchop) ---------------------------

/**
 * Detect transient onsets in an audio buffer using envelope-following
 * onset detection. Returns array of sample positions where slices start.
 *
 * @param {Float32Array} mono - Mono audio data (-1..1)
 * @param {number} sampleRate - Sample rate in Hz
 * @param {object} opts - { sensitivity: 1-5, minSliceMs: 20-500 }
 * @returns {number[]} Array of onset sample positions
 */
export function detectTransients(mono, sampleRate, opts = {}) {
   const sensitivity = opts.sensitivity ?? 1.5;
   const minSliceMs = opts.minSliceMs ?? 80;
   const minSliceSamples = Math.floor((minSliceMs / 1000) * sampleRate);
   const totalSamples = mono.length;

   if (totalSamples < minSliceSamples) return [0];

   // Stage 1: Envelope follower (analog-style)
   const attackTC = sampleRate * 0.0003;
   const releaseTC = sampleRate * 0.015;
   const attackCoeff = Math.exp(-1 / attackTC);
   const releaseCoeff = Math.exp(-1 / releaseTC);

   const envelope = new Float32Array(totalSamples);
   let env = 0;
   for (let i = 0; i < totalSamples; i++) {
      const x = Math.abs(mono[i]);
      if (x > env) {
         env = attackCoeff * env + (1 - attackCoeff) * x;
      } else {
         env = releaseCoeff * env + (1 - releaseCoeff) * x;
      }
      envelope[i] = env;
   }

   // Stage 2: Log compression
   const logEnv = new Float32Array(totalSamples);
   const logScale = 1000;
   const logBase = Math.log(1 + logScale);
   for (let i = 0; i < totalSamples; i++) {
      logEnv[i] = Math.log(1 + envelope[i] * logScale) / logBase;
   }

   // Stage 3: Derivative (onset detection function)
   const hop = 32;
   const numFrames = Math.floor(totalSamples / hop);
   const odf = new Float32Array(numFrames);
   for (let f = 1; f < numFrames; f++) {
      const prev = logEnv[(f - 1) * hop];
      const curr = logEnv[f * hop];
      odf[f] = Math.max(0, curr - prev);
   }

   // Stage 4: Smooth ODF
   const smoothOdf = new Float32Array(numFrames);
   for (let f = 0; f < numFrames; f++) {
      let sum = 0, count = 0;
      for (let k = -1; k <= 1; k++) {
         const idx = f + k;
         if (idx >= 0 && idx < numFrames) { sum += odf[idx]; count++; }
      }
      smoothOdf[f] = sum / count;
   }

   // Stage 5: Normalize
   let maxOdf = 0;
   for (let f = 0; f < numFrames; f++) {
      if (smoothOdf[f] > maxOdf) maxOdf = smoothOdf[f];
   }
   if (maxOdf <= 0) return [0];
   for (let f = 0; f < numFrames; f++) smoothOdf[f] /= maxOdf;

   // Stage 6: Peak picking
   const threshold = 0.15 / sensitivity;
   const peaks = [];
   let lastPeakSample = -minSliceSamples;

   for (let f = 2; f < numFrames - 2; f++) {
      if (smoothOdf[f] < threshold) continue;
      if (smoothOdf[f] <= smoothOdf[f - 1] || smoothOdf[f] < smoothOdf[f + 1]) continue;

      const samplePos = f * hop;
      if (samplePos - lastPeakSample < minSliceSamples) continue;

      // Stage 7: Refine onset
      const lookback = Math.floor(sampleRate * 0.02 / hop);
      let onsetFrame = f;
      let minEnv = logEnv[f * hop];
      for (let b = 1; b <= lookback && f - b > 0; b++) {
         const e = logEnv[(f - b) * hop];
         if (e <= minEnv) {
            minEnv = e;
            onsetFrame = f - b;
         }
         if (e > minEnv * 1.05 && b > 2) break;
      }

      const onsetSample = onsetFrame * hop;
      if (onsetSample - lastPeakSample < minSliceSamples) continue;

      peaks.push(onsetSample);
      lastPeakSample = onsetSample;
   }

   if (peaks.length === 0 || peaks[0] > minSliceSamples) {
      peaks.unshift(0);
   }

   return peaks;
}

/**
 * Create slice definitions from onset positions.
 *
 * @param {number[]} onsets - Array of onset sample positions
 * @param {number} totalSamples - Total length of audio
 * @returns {Array<{start, end, name}>} Slice definitions
 */
export function slicesFromOnsets(onsets, totalSamples) {
   const slices = [];
   for (let i = 0; i < onsets.length; i++) {
      const start = onsets[i];
      const end = i < onsets.length - 1 ? onsets[i + 1] : totalSamples;
      slices.push({ start, end, name: `slice_${i + 1}` });
   }
   return slices;
}

// ---- REX2 IFF file builder (ported from breakchop) -------------------------

function writeFourCC(view, offset, str) {
   for (let i = 0; i < 4; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
   }
}

function chunkSize(chunk) {
   if (chunk.id === 'CAT ') {
      let size = 4;
      for (const sub of chunk.subChunks) {
         size += chunkSize(sub);
      }
      return 8 + size;
   }
   let s = 8 + chunk.data.byteLength;
   if (chunk.data.byteLength & 1) s++;
   return s;
}

function writeChunk(buf, view, pos, chunk) {
   if (chunk.id === 'CAT ') {
      let innerSize = 4;
      for (const sub of chunk.subChunks) {
         innerSize += chunkSize(sub);
      }
      writeFourCC(view, pos, 'CAT ');
      view.setUint32(pos + 4, innerSize, false);
      writeFourCC(view, pos + 8, chunk.formId);
      pos += 12;
      for (const sub of chunk.subChunks) {
         pos = writeChunk(buf, view, pos, sub);
      }
      return pos;
   }
   writeFourCC(view, pos, chunk.id);
   view.setUint32(pos + 4, chunk.data.byteLength, false);
   const srcBytes = new Uint8Array(chunk.data);
   buf.set(srcBytes, pos + 8);
   pos += 8 + chunk.data.byteLength;
   if (chunk.data.byteLength & 1) pos++;
   return pos;
}

function buildRex2IFF(topChunks) {
   let innerSize = 4;
   for (const chunk of topChunks) {
      innerSize += chunkSize(chunk);
   }
   const totalSize = 8 + innerSize;
   const buf = new Uint8Array(totalSize);
   const view = new DataView(buf.buffer);

   writeFourCC(view, 0, 'CAT ');
   view.setUint32(4, innerSize, false);
   writeFourCC(view, 8, 'REX2');

   let pos = 12;
   for (const chunk of topChunks) {
      pos = writeChunk(buf, view, pos, chunk);
   }

   return buf;
}

// ---- Main export function --------------------------------------------------

/**
 * Build a complete REX2 (.rx2) file from audio data and slice definitions.
 *
 * @param {object} audio - { pcm: Int32Array[] (per channel), sampleRate, numChannels, frameCount }
 * @param {Array} slices - [{ start, end }] slice definitions
 * @param {object} opts - { bpm, fileName }
 * @returns {Uint8Array} REX2 file bytes
 */
export function buildRex2(audio, slices, opts = {}) {
   const { pcm, sampleRate, numChannels, frameCount } = audio;
   const bpm = opts.bpm || 120;
   const tempo = Math.round(bpm * 1000);
   const fileName = (opts.fileName || 'xtrc') + '.rx2';

   // DWOP-encode the audio
   let sdatBytes;
   if (numChannels === 1) {
      sdatBytes = encodeMono(pcm[0]);
   } else {
      sdatBytes = encodeStereo(pcm[0], pcm[1] || pcm[0]);
   }

   const chunks = [];

   // HEAD chunk
   const shortName = fileName.slice(0, 11);
   const headSize = 29 + shortName.length;
   const headData = new ArrayBuffer(headSize);
   const headView = new DataView(headData);
   headView.setUint32(0, 0x490cf18d, false);    // magic
   headView.setUint32(4, 0xbc020000, false);    // version
   headView.setUint8(28, shortName.length);
   for (let i = 0; i < shortName.length; i++) {
      headView.setUint8(29 + i, shortName.charCodeAt(i));
   }
   chunks.push({ id: 'HEAD', data: headData });

   // GLOB chunk
   const totalSlceCount = slices.length + 1;
   const globData = new ArrayBuffer(22);
   const globView = new DataView(globData);
   globView.setUint32(0, totalSlceCount, false);
   globView.setUint16(4, 4, false);
   globView.setUint16(6, 4, false);
   globView.setUint8(8, 4);
   globView.setUint8(9, 0);
   globView.setUint32(10, 0x3e8, false);
   globView.setUint16(14, 0, false);
   globView.setUint32(16, tempo, false);
   globView.setUint16(20, 0x0100, false);
   chunks.push({ id: 'GLOB', data: globData });

   // RECY chunk
   const recyData = new ArrayBuffer(15);
   const recyView = new DataView(recyData);
   recyView.setUint32(0, 0xbc020000, false);
   recyView.setUint32(4, 0x00010000, false);
   recyView.setUint32(8, 0, false);
   recyView.setUint8(12, 0);
   recyView.setUint8(13, 0);
   recyView.setUint8(14, 0);
   chunks.push({ id: 'RECY', data: recyData });

   // CAT DEVL
   const devlChunks = [];
   const trshData = new ArrayBuffer(7);
   const trshView = new DataView(trshData);
   trshView.setUint8(0, 0x01);
   trshView.setUint8(1, 0x00);
   trshView.setUint8(2, 0x03);
   trshView.setUint8(3, 0x03);
   trshView.setUint8(4, 0xff);
   trshView.setUint8(5, 0x00);
   trshView.setUint8(6, 0x32);
   devlChunks.push({ id: 'TRSH', data: trshData });
   const eqData = new ArrayBuffer(17);
   const eqView = new DataView(eqData);
   const eqBytes = [0x00, 0x00, 0x0f, 0x00, 0x64, 0x00, 0x00, 0x03, 0xe8, 0x09, 0xc4, 0x00, 0x00, 0x03, 0xe8, 0x4e, 0x20];
   for (let i = 0; i < 17; i++) eqView.setUint8(i, eqBytes[i]);
   devlChunks.push({ id: 'EQ  ', data: eqData });
   const compData = new ArrayBuffer(9);
   const compView = new DataView(compData);
   const compBytes = [0x00, 0x00, 0x4d, 0x00, 0x27, 0x00, 0xc3, 0x01, 0x64];
   for (let i = 0; i < 9; i++) compView.setUint8(i, compBytes[i]);
   devlChunks.push({ id: 'COMP', data: compData });
   chunks.push({ id: 'CAT ', formId: 'DEVL', subChunks: devlChunks });

   // CAT SLCL
   const slclChunks = [];
   // Placeholder slice
   const placeholderData = new ArrayBuffer(11);
   const placeholderView = new DataView(placeholderData);
   placeholderView.setUint32(0, 0, false);
   placeholderView.setUint32(4, 1, false);
   placeholderView.setUint16(8, 0x0400, false);
   placeholderView.setUint8(10, 0x00);
   slclChunks.push({ id: 'SLCE', data: placeholderData });
   // Real slices
   for (let i = 0; i < slices.length; i++) {
      const slice = slices[i];
      const start = slice.start;
      const end = (i < slices.length - 1) ? slices[i + 1].start : frameCount;
      const length = end - start;
      const slceData = new ArrayBuffer(11);
      const slceView = new DataView(slceData);
      slceView.setUint32(0, start, false);
      slceView.setUint32(4, length, false);
      slceView.setUint16(8, 0x7fff, false);
      slceView.setUint8(10, 0x02);
      slclChunks.push({ id: 'SLCE', data: slceData });
   }
   chunks.push({ id: 'CAT ', formId: 'SLCL', subChunks: slclChunks });

   // SINF chunk
   const sinfData = new ArrayBuffer(18);
   const sinfView = new DataView(sinfData);
   sinfView.setUint8(0, numChannels);
   sinfView.setUint8(1, 0x03);  // 16-bit
   sinfView.setUint32(2, sampleRate, false);
   sinfView.setUint32(6, frameCount, false);
   const loopStart = slices.length > 0 ? slices[0].start : 0;
   sinfView.setUint32(10, loopStart, false);
   sinfView.setUint32(14, frameCount, false);
   chunks.push({ id: 'SINF', data: sinfData });

   // SDAT chunk
   chunks.push({ id: 'SDAT', data: sdatBytes.buffer.slice(sdatBytes.byteOffset, sdatBytes.byteOffset + sdatBytes.byteLength) });

   return buildRex2IFF(chunks);
}
